import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const useSessionTimeout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, sessionTimeout } = useSelector(state => state.userState);
  
  const timeoutRef = useRef(null);
  
  // Convert minutes to milliseconds
  const timeoutDuration = sessionTimeout ? sessionTimeout * 60 * 1000 : 30 * 60 * 1000; // Default 30 minutes
  
  const logout = useCallback(() => {
    // console.log('Session timeout - logging out user');
    
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear Redux state - Fixed action type to match rootReducer
    dispatch({ type: 'CLEAR_STORE' });
    
    // Show logout message with enhanced configuration
    toast.info('Session expired. You have been logged out due to inactivity.', {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'session-expired' // Prevent duplicate toasts
    });
    
    // Small delay to ensure toast is shown before navigation
    setTimeout(() => {
      navigate('/');
    }, 100);
  }, [dispatch, navigate]);
  
  const resetTimeout = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Only set timeout if user is logged in and sessionTimeout is valid
    if (token && timeoutDuration > 0) {
      // console.log(`Setting session timeout for ${timeoutDuration / 1000 / 60} minutes`);
      // Set logout timeout
      timeoutRef.current = setTimeout(logout, timeoutDuration);
    }
  }, [token, timeoutDuration, logout]);
  
  // Activity event handlers
  const handleActivity = useCallback(() => {
    if (token) {
      resetTimeout();
    }
  }, [token, resetTimeout]);
  
  useEffect(() => {
    if (!token) {
      // Clear timeout if user is not logged in
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }
    
    // Validate sessionTimeout
    if (!sessionTimeout || sessionTimeout <= 0) {
      console.warn('Invalid sessionTimeout value:', sessionTimeout);
      return;
    }
    
    // Start the timeout when component mounts or token changes
    resetTimeout();
    
    // Activity events to monitor
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];
    
    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });
    
    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [token, sessionTimeout, handleActivity, resetTimeout]);
  
  return {
    resetTimeout,
    logout
  };
};

export default useSessionTimeout;